import Delaunator from 'delaunator';

export const triangulatePoints = (points) => {
  if (points.length < 3) return { triangles: [], edges: [] };
  
  const coords = points.flatMap(p => [p.x, p.y]);
  const delaunay = Delaunator.from(points, p => p.x, p => p.y);
  
  const triangles = [];
  for (let i = 0; i < delaunay.triangles.length; i += 3) {
    const a = points[delaunay.triangles[i]];
    const b = points[delaunay.triangles[i + 1]];
    const c = points[delaunay.triangles[i + 2]];
    triangles.push([a, b, c]);
  }
  
  return { triangles, delaunay };
};

export const generateContourLines = (triangles, points, interval) => {
  if (!triangles || triangles.length === 0 || !interval || interval <= 0) {
    return [];
  }
  
  const minZ = Math.min(...points.map(p => p.z));
  const maxZ = Math.max(...points.map(p => p.z));
  
  const contourLines = [];
  
  // Generar líneas de contorno para cada nivel
  for (let level = Math.ceil(minZ / interval) * interval; level <= maxZ; level += interval) {
    const levelLines = [];
    
    triangles.forEach(triangle => {
      const [p1, p2, p3] = triangle;
      const intersections = [];
      
      // Verificar intersecciones en cada arista del triángulo
      const edges = [[p1, p2], [p2, p3], [p3, p1]];
      
      edges.forEach(([a, b]) => {
        if ((a.z <= level && b.z >= level) || (a.z >= level && b.z <= level)) {
          if (a.z !== b.z) {
            const t = (level - a.z) / (b.z - a.z);
            const x = a.x + t * (b.x - a.x);
            const y = a.y + t * (b.y - a.y);
            intersections.push({ x, y, z: level });
          }
        }
      });
      
      // Si hay exactamente 2 intersecciones, crear una línea
      if (intersections.length === 2) {
        levelLines.push({
          start: intersections[0],
          end: intersections[1],
          level: level
        });
      }
    });
    
    if (levelLines.length > 0) {
      contourLines.push({
        level: level,
        lines: levelLines
      });
    }
  }
  
  return contourLines;
};

export const interpolateZ = (x, y, triangles) => {
  // Encontrar el triángulo que contiene el punto (x, y)
  for (const triangle of triangles) {
    const [p1, p2, p3] = triangle;
    
    if (isPointInTriangle(x, y, p1, p2, p3)) {
      // Interpolación baricéntrica
      const denom = (p2.y - p3.y) * (p1.x - p3.x) + (p3.x - p2.x) * (p1.y - p3.y);
      const a = ((p2.y - p3.y) * (x - p3.x) + (p3.x - p2.x) * (y - p3.y)) / denom;
      const b = ((p3.y - p1.y) * (x - p3.x) + (p1.x - p3.x) * (y - p3.y)) / denom;
      const c = 1 - a - b;
      
      return a * p1.z + b * p2.z + c * p3.z;
    }
  }
  
  return null;
};

const isPointInTriangle = (x, y, p1, p2, p3) => {
  const denom = (p2.y - p3.y) * (p1.x - p3.x) + (p3.x - p2.x) * (p1.y - p3.y);
  const a = ((p2.y - p3.y) * (x - p3.x) + (p3.x - p2.x) * (y - p3.y)) / denom;
  const b = ((p3.y - p1.y) * (x - p3.x) + (p1.x - p3.x) * (y - p3.y)) / denom;
  const c = 1 - a - b;
  
  return a >= 0 && b >= 0 && c >= 0;
};